import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {MatStepper} from '@angular/material/stepper';
import {Router} from '@angular/router';
import {Subject} from 'rxjs';
import {map, takeUntil} from 'rxjs/operators';
import {AuthenticationService, LUXENGINE_LOGO_ON_LIGHT, StripeService} from '../../services';
import {BRANDS} from '../../../luxengine-brand-kit/brands';

@Component({
    selector: 'prism-first-time-account-setup',
    templateUrl: './first-time-account-setup.component.html',
    styleUrls: ['./first-time-account-setup.component.scss']
})
export class FirstTimeAccountSetupComponent implements OnInit, OnDestroy {

    stepsCompleted = {
        0: false,
        1: false,
        2: false
    };

    memberData$ = this.authService.memberData$;
    isLuxEngine$ = this.authService.isLuxEngine$;
    luxEngineName = BRANDS.luxengine.displayName;
    luxEngineLogo = LUXENGINE_LOGO_ON_LIGHT;
    // CONTRACTORS AND MEMBERS WITH A 'bypass' STRIPE ID DO NOT ADD A CARD
    showPaymentStep$ = this.memberData$.pipe(
        map(member => !!member && !member.isContractor && member.stripeId !== 'bypass')
    );
    member: any;
    destroySub$ = new Subject();
    agreementAcceptedInSession = false;

    @ViewChild('stepper', {static: true}) stepper: MatStepper;

    constructor(private router: Router, private authService: AuthenticationService, private stripeService: StripeService) {
    }

    ngOnInit() {
        this.memberData$.pipe(takeUntil(this.destroySub$)).subscribe(val => {
            this.member = val;
            const {acceptedPlatformPolicies} = this.member ?? {acceptedPlatformPolicies: false};

            if (acceptedPlatformPolicies) {
                this.completeStep(0);
            }

            if (val.firstName && val.lastName && val.secondaryEmail && val.contactNumber && this.member.companyName && this.member.companyId) {
                this.completeStep(1);
            }
        });

        this.stepper.selectionChange
            .subscribe(selection => {
                // INDEX 1 IS USER/MEDIA AGREEMENT
                if (selection.selectedIndex === 0 && this.member?.acceptedPlatformPolicies) {
                    this.completeStep(0);
                }
            });
    }

    ngOnDestroy() {
        this.destroySub$.next();
    }

    getMember(): Promise<any> {
        return new Promise(resolve => {
            this.memberData$.pipe(takeUntil(this.destroySub$)).subscribe(val => {
                this.member = val;
                resolve(this.member);
            });
        });
    }

    onPlatformPolicyAccepted() {
        this.agreementAcceptedInSession = true;
        this.completeStep(0);
    }

    completeStep(stepIndex: number) {
        this.stepsCompleted[stepIndex] = true;

        setTimeout(() => {
            // INDEX 0 IS PLATFORM AGREEMENT
            if (stepIndex === 0) {
                this.member.acceptedPlatformPolicies = true;
            }

            this.stepper.next();
        }, 100);

        if (!this.member) {
            this.getMember().then(() => {
                this.checkMustNavHome();
            });
        } else if (!!this.member) {
            this.checkMustNavHome();
        }
    }

    checkMustNavHome() {
        if (this.agreementAcceptedInSession) {
            return;
        }

        if ((this.stepsCompleted[0] || this.member?.acceptedPlatformPolicies) && this.member.lastName && this.member.firstName && this.member.loginEmail) {
            this.hasPaymentMethod().then(hasPaymentMethod => {
                if (hasPaymentMethod && !this.agreementAcceptedInSession) {
                    this.goHome();
                }
            });
        }
    }


    hasPaymentMethod(): Promise<boolean> {
        const {isContractor, stripeId} = this.member ?? {} as any;

        if (isContractor || stripeId === 'bypass') {
            return Promise.resolve(true);
        }

        if (!stripeId) {
            return Promise.resolve(false);
        }

        return this.stripeService.retrieveCustomer(stripeId)
            .then(customer => !!customer?.['data']?.invoice_settings?.default_payment_method)
            .catch(() => false);
    }

    goHome() {
        this.router.navigate(['home', 'jobs']);
    }

    completeAccountDetails() {
        this.agreementAcceptedInSession = false;
        this.completeStep(1);

        if (!!this.stepsCompleted[0] || this.member?.acceptedPlatformPolicies) {
            this.hasPaymentMethod().then(hasPaymentMethod => {
                if (hasPaymentMethod) {
                    this.goHome();
                }
            });
        }
    }
}
